import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import { SnackbarSeverityType } from '../../enums';
import { PageContainer, HeaderBox } from './styled';
import ActionToolbar from './components/ActionToolbar';
import { FeeManagementSection } from './components/FeeManagementSection';
import studentProfile from '../../apis/studentProfile';
import paymentSchemesApis from '../../apis/payments/paymentSchemes';
import {
  StudentPaymentSchemeWithMetrics,
  StudentSchemesAndTransactionsResponse,
  StudentTransactionMatrixItem
} from '../../types/paymentScheme';
import StudentDescriptionTile from './components/StudentDescriptionTile';
import { transformStudentDetailData } from '../../utils/studentDataTransformer';
import { TransformedStudentDetail } from '../../types/userType';
import Icons from '../../components/icons';

interface StudentFeeEntryPageProps {
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
  studentId: string;
  onBack: () => void;
}

const StudentFeeEntryPage: React.FC<StudentFeeEntryPageProps> = ({
  onShowToast,
  studentId,
  onBack
}) => {
  const [userDetails, setUserDetails] =
    useState<TransformedStudentDetail | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [studentPaymentSchemes, setStudentPaymentSchemes] = useState<
    StudentPaymentSchemeWithMetrics[]
  >([]);
  const [studentTransactions, setStudentTransactions] = useState<
    StudentTransactionMatrixItem[]
  >([]);
  const [isLoadingFeeDetails, setIsLoadingFeeDetails] = useState(true);
  const [academicYear, setAcademicYear] = useState('All');
  const [componentType, setComponentType] = useState('All');
  const [particulars, setParticulars] = useState('All');

  const fetchFeeDetails = useCallback(async () => {
    setIsLoadingFeeDetails(true);
    try {
      const response: StudentSchemesAndTransactionsResponse =
        await paymentSchemesApis.fetchStudentPaymentSchemesWithMetrics(
          studentId,
          componentType === 'All' ? undefined : componentType,
          particulars === 'All' ? undefined : particulars,
          academicYear === 'All' ? undefined : academicYear
        );
      setStudentPaymentSchemes(response.paymentSchemesMatrix);
      setStudentTransactions(response.transactionsMatrix);
    } catch (error: any) {
      console.error('Error fetching student payment schemes:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to fetch fee details.',
        SnackbarSeverityType.error
      );
      setStudentPaymentSchemes([]);
      setStudentTransactions([]);
    } finally {
      setIsLoadingFeeDetails(false);
    }
  }, [studentId, componentType, particulars, academicYear, onShowToast]);

  useEffect(() => {
    const getStudentDetails = async () => {
      setIsLoadingDetails(true);
      try {
        const response = await studentProfile.fetchStudentDetailApi({
          studentIds: [studentId]
        });
        const transformedStudentDetailData =
          transformStudentDetailData(response);
        if (transformedStudentDetailData) {
          setUserDetails(transformedStudentDetailData);
        } else {
          onShowToast('Student details not found', SnackbarSeverityType.error);
        }
      } catch (error: any) {
        console.error('Error fetching student details:', error);
        onShowToast(
          error?.errors?.[0]?.message || 'Error getting student details',
          SnackbarSeverityType.error
        );
        setUserDetails(null);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    if (studentId) {
      getStudentDetails();
    }
  }, [studentId, onShowToast]);

  useEffect(() => {
    if (studentId) {
      fetchFeeDetails();
    }
  }, [studentId, componentType, particulars, academicYear, fetchFeeDetails]);

  return (
    <PageContainer>
      <HeaderBox>
        <IconButton onClick={onBack} sx={{ mr: 1 }}>
          <Icons.CaretLeft />
        </IconButton>
        <Typography variant='h4' fontWeight='bold'>
          Student Fee Entry
        </Typography>
      </HeaderBox>
      {isLoadingDetails ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100px'
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ pt: 0 }}>
          {userDetails && <StudentDescriptionTile studentData={userDetails} />}

          <ActionToolbar
            userDetails={userDetails}
            onShowToast={onShowToast}
            onRefreshFeeDetails={fetchFeeDetails}
            studentPaymentSchemes={studentPaymentSchemes}
          />
          <FeeManagementSection
            onShowToast={onShowToast}
            studentId={studentId}
            userDetails={userDetails}
            studentPaymentSchemes={studentPaymentSchemes}
            studentTransactions={studentTransactions}
            isLoadingFeeDetails={isLoadingFeeDetails}
            fetchFeeDetails={fetchFeeDetails}
            academicYear={academicYear}
            setAcademicYear={setAcademicYear}
            componentType={componentType}
            setComponentType={setComponentType}
            particulars={particulars}
            setParticulars={setParticulars}
          />
        </Box>
      )}
    </PageContainer>
  );
};

export default StudentFeeEntryPage;
